import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, Disc3, Gamepad2, ListChecks, Gift, ChevronRight, Target, TrendingUp } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { levelFor } from "@/lib/levels";
import { useCountUp } from "@/components/PointsBurst";
import { UserAvatar } from "@/components/UserBits";

const greet = () => { const h = new Date().getHours(); return h < 11 ? "Guten Morgen" : h < 17 ? "Schön, dass du da bist" : h < 22 ? "Guten Abend" : "Gute Nacht"; };
const fmt = (n) => Number(n || 0).toLocaleString("de-DE");

export function useHome() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get("/home").then(({ data }) => setD(data)).catch(() => {});
    load();
    window.addEventListener("points-awarded", load);
    window.addEventListener("goal-changed", load);
    return () => { window.removeEventListener("points-awarded", load); window.removeEventListener("goal-changed", load); };
  }, []);
  return d;
}

export const SheenBar = ({ pct, testId, className = "h-2.5", fill = "bg-gradient-to-r from-amber-200 via-amber-300 to-amber-500", track = "bg-white/25" }) => (
  <div className={`sheen-track ${track} ${className}`}><div className={`sheen-fill ${fill}`} style={{ width: `${Math.max(3, Math.min(100, pct))}%` }} data-testid={testId} /></div>
);

function GoalStrip({ goal, next }) {
  const r = goal || next;
  if (!r) return null;
  const ready = goal && goal.missing === 0;
  return (
    <Link to="/geschenke" data-testid="home-goal" className="tap mt-4 flex items-center gap-3 p-3 rounded-2xl lux-chip">
      <span className="w-12 h-12 rounded-xl overflow-hidden bg-white/30 shrink-0 ring-1 ring-white/50">{r.image ? <img src={imgSrc(r.image)} alt={r.title} className="w-full h-full object-cover" /> : <Gift className="w-6 h-6 m-3" />}</span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-white/80"><Target className="w-3 h-3" /> {goal ? "Deine Wunschprämie" : "Nächste Prämie"}</span>
        <span className="block text-sm font-extrabold truncate" data-testid="home-goal-title">{r.title}</span>
        <SheenBar pct={r.pct} testId="home-goal-bar" className="h-2 mt-1.5" />
        <span className="block mt-1 text-[11px] font-bold text-white/90" data-testid="home-goal-missing">{ready ? "Du kannst sie jetzt einlösen! 🎉" : `Noch ${fmt(r.missing)} Punkte · ${String(r.pct).replace(".", ",")} %`}</span>
      </span>
      <ChevronRight className="w-5 h-5 shrink-0" />
    </Link>
  );
}

const Chip = ({ to, icon: Icon, label, value, hot, testId }) => (
  <Link to={to} data-testid={testId} className={`tap relative flex flex-col gap-0.5 p-2.5 rounded-2xl lux-chip min-w-0 ${hot ? "ring-2 ring-amber-300/80" : ""}`}>
    {hot && <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-amber-300 shadow-[0_0_8px_#fcd34d]" />}
    <Icon className="w-4 h-4 text-white/90" />
    <span className="text-[10px] font-bold text-white/80 leading-tight truncate">{label}</span>
    <span className="text-sm font-extrabold leading-tight truncate">{value}</span>
  </Link>
);

export function HomeHero() {
  const { user } = useAuth();
  const d = useHome();
  const points = user?.points ?? d?.points ?? 0;
  const shown = useCountUp(points, 1100, 2000);
  const lvl = levelFor(points);
  if (!user) return null;
  return (
    <section data-testid="home-hero">
      <div className="lux-hero relative overflow-hidden rounded-[2rem] p-5 sm:p-6 text-white fade-up">
        <span className="lux-hero-orb w-48 h-48 -top-16 -right-10" />
        <span className="lux-hero-orb w-28 h-28 bottom-6 -left-10" style={{ animationDelay: "-4s" }} />
        <div className="relative flex items-center gap-3">
          <Link to={`/u/${user.user_id}`} data-testid="home-avatar" className="shrink-0 rounded-full ring-2 ring-white/60"><UserAvatar name={user.name} picture={user.picture} userId={user.user_id} size="w-12 h-12" noStory /></Link>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-white/85">{greet()},</p>
            <p className="font-display text-xl font-extrabold leading-tight truncate" data-testid="home-name">{user.name?.split(" ")[0]}</p>
          </div>
          <span className="shrink-0 px-3 h-8 inline-flex items-center gap-1 rounded-full lux-chip text-xs font-extrabold" data-testid="home-level">{lvl.emoji} Lvl {lvl.level} · {lvl.name}</span>
        </div>
        <div className="relative mt-5 flex items-end justify-between gap-3">
          <Link to="/dashboard" data-testid="home-points" className="min-w-0">
            <span className="block text-[11px] font-extrabold uppercase tracking-[0.25em] text-white/75">Dein Punktestand</span>
            <span className="flex items-center gap-2 font-display text-4xl sm:text-5xl font-black tabular-nums drop-shadow-sm"><Coins className="w-8 h-8 text-amber-200" /> <span data-testid="home-points-value">{fmt(shown)}</span></span>
          </Link>
          {d && <span className="shrink-0 mb-1 inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-white text-rose-600 text-xs font-extrabold shadow" data-testid="home-today-points"><TrendingUp className="w-3.5 h-3.5" /> +{fmt(d.today_points)} heute</span>}
        </div>
        <div className="relative mt-3" data-testid="home-level-progress">
          <SheenBar pct={lvl.progress * 100} testId="home-level-bar" className="h-2" fill="bg-gradient-to-r from-white via-pink-100 to-white" />
          <p className="mt-1 text-[11px] font-bold text-white/85">{lvl.next ? `Noch ${fmt(lvl.missing)} Punkte bis Level ${lvl.level + 1} „${lvl.next.name}“` : "Höchstes Level erreicht 💎"}</p>
        </div>
        {d && <GoalStrip goal={d.goal} next={d.next} />}
        {d && (
          <div className="relative mt-3 grid grid-cols-3 gap-2">
            <Chip to="/dashboard" icon={ListChecks} label="Tagesaufgaben" value={`${d.tasks.done}/${d.tasks.total}`} hot={d.tasks.done < d.tasks.total} testId="home-chip-tasks" />
            <Chip to="/spiele/gluecksrad" icon={Disc3} label="Glücksrad" value={d.wheel_available ? "Bereit!" : "Morgen wieder"} hot={d.wheel_available} testId="home-chip-wheel" />
            <Chip to="/spiele" icon={Gamepad2} label="Skill-Spiele" value={d.skill_left ? `${d.skill_left}× offen` : "Alle gespielt"} hot={d.skill_left > 0} testId="home-chip-skill" />
          </div>
        )}
      </div>
    </section>
  );
}
